// src/pages/FiltersPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import BookCard from '../components/BookCard'
import {
  collection, query, where, orderBy,
  limit, startAfter, getDocs, documentId   
} from 'firebase/firestore';
import { getResponsivePageSize } from '../utils/pagination';
import { getFavoriteCafeIds } from '../utils/favoriteCafes';
import { isReservationActive, listReservationsByBookIds } from '../utils/reservations';

// Коди районів Києва (slug → label)
const KYIV_DISTRICTS = [
  { slug: 'shevchenkivskyi', label: 'Шевченківський' },
  { slug: 'pecherskyi',     label: 'Печерський' },
  { slug: 'solomianskyi',   label: 'Солом’янський' },
  { slug: 'holosiivskyi',   label: 'Голосіївський' },
  { slug: 'svjatoshynskyi', label: 'Святошинський' },
  { slug: 'obolonskyi',     label: 'Оболонський' },
  { slug: 'podilskyi',      label: 'Подільський' },
  { slug: 'dniprovskyi',    label: 'Дніпровський' },
  { slug: 'desnianskyi',    label: 'Деснянський' },
  { slug: 'darniytskyi',    label: 'Дарницький' },
];

// швидкий доступ: slug → label
const DISTRICT_LABEL = KYIV_DISTRICTS.reduce((acc, d) => {
  acc[d.slug] = d.label;
  return acc;
}, {});

// нормалізує рядок/поле до slug (нижній регістр, без пробілів/діакритики)
const toDistrictSlug = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return '';

  // якщо вже валідний slug з нашого списку — повертаємо як є
  if (DISTRICT_LABEL[v]) return v;

  // грубе нормалізування: прибираємо "район/р-н", замінюємо апострофи тощо
  let s = v
    .replace(/район|р-н/gi, '')
    .replace(/[’'`]/g, '')
    .replace(/\s+/g, '')
    .replace(/ї/g, 'i')
    .replace(/й/g, 'y')
    .replace(/є/g, 'e')
    .replace(/ю/g, 'yu')
    .replace(/я/g, 'ya')
    .replace(/і/g, 'i')
    .replace(/ґ/g, 'g')
    .replace(/ь/g, '');

  // інколи адреса типу "м. київ" — тоді повернемо порожньо
  if (s === 'м.київ' || s === 'мкиїв' || s === 'київ' || s === 'kyiv') return '';

  return s;
};

// Людські назви категорій (як на сторінці книги)
const mapCategory = (raw) => {
  const v = (raw || '').toString().trim().toLowerCase();
  switch (v) {
    case 'fiction':
      return 'Історії, що захоплюють';
    case 'non-fiction':
    case 'nonfiction':
      return 'Дізнатись нове';
    case 'kids':
    case 'children':
    case 'child':
    case 'kids & teens':
      return 'Для дітей';
    default:
      return raw || '—';
  }
};

// універсальний ключ книги (для мерджу)
const keyOf = (b) => {
  const isbn = (b.isbn || b.ISBN || b.isbn13 || '').replace(/[^0-9A-Za-z]/g, '').toLowerCase();
  if (isbn) return `isbn:${isbn}`;
  const t = (b.title || b.name || '').toLowerCase().trim();
  const a = (b.author || (b.authors?.join(', ') || '')).toLowerCase().trim();
  return `ta:${t}__${a}`;
};

const FILTERS_BOOTSTRAP_TTL_MS = 5 * 60 * 1000;
let filtersBootstrapCache = null;

// Акордеон-секція з «пташкою» та можливістю згортати
// Акордеон-секція з «пташкою» та стрілкою
// Акордеон-секція без чекбокса в шапці
// Акордеон-секція БЕЗ лічильника
function MultiSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="flt-section">
      <div className="flt-head">
        <div className="flt-title-wrap">
          <span className="flt-title">{title}</span>
        </div>
        <button className="flt-chevron" onClick={() => setOpen(o => !o)} aria-label="Перемкнути">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d={open ? 'M6 15l6-6 6 6' : 'M9 6l6 6-6 6'}
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      {open && <div className="flt-body">{children}</div>}
    </div>
  );
}


/* ---------- сторінка ---------- */

export default function FiltersPage() {
const [user] = useAuthState(auth);
  // довідники
const [cafes, setCafes] = useState([]);            // {name, address, district}
const [favoriteCafeIds, setFavoriteCafeIds] = useState([]);

const [categories, setCategories] = useState([]);  // сирі ключі
const [subcategories, setSubcategories] = useState([]);
const [templateCatalog, setTemplateCatalog] = useState([]);

// межі цін
const [priceBounds, setPriceBounds] = useState([0, 400]);
// з порожніх значень, щоб не стріляти запитами кожним кейстроком
const [priceMin, setPriceMin] = useState('');
const [priceMax, setPriceMax] = useState('');


// результати (мають бути вище за districts)
const [loading, setLoading] = useState(true);
const [books, setBooks] = useState([]);
const [asideTop, setAsideTop] = useState(76);
const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
const [mobileFiltersOpen, setMobileFiltersOpen] = useState(true);
const [mobileHeadTop, setMobileHeadTop] = useState(0);

// мапа "назва кав'ярні -> район" (має бути вище за districts)
const cafeDistrictByName = useMemo(() => {
  const m = new Map();
  cafes.forEach(c => {
    const name = (c.name || '').trim();
    const slug = toDistrictSlug(c.district || c.districtSlug || c.area);
    if (name && slug) m.set(name, slug);
  });
  return m;
}, [cafes]);


// список доступних районів (slug), взятий із cafes та/або books→cafeName
const districts = useMemo(() => {
  const s = new Set();

  // 1) з кав'ярень
  cafes.forEach(c => {
    const slug = toDistrictSlug(c.district || c.districtSlug);
    if (slug) s.add(slug);
  });

  // 2) fallback з екземплярів книг через назву кафе
  books.forEach(b => {
    if (b.__col === 'books' && b.cafeName) {
      const slug = toDistrictSlug(cafeDistrictByName.get(b.cafeName));
      if (slug) s.add(slug);
    }
  });

  // відсортуємо за людськими назвами
  return Array.from(s).sort((a, b) => {
    const la = DISTRICT_LABEL[a] || a;
    const lb = DISTRICT_LABEL[b] || b;
    return la.localeCompare(lb);
  });
}, [cafes, books, cafeDistrictByName]);


// вибрані фільтри
const [districtSet, setDistrictSet] = useState(new Set());
const [cafeSet, setCafeSet] = useState(new Set());
const [categorySet, setCategorySet] = useState(new Set());
const [subcatSet, setSubcatSet] = useState(new Set());
const [appliedFilters, setAppliedFilters] = useState({
  districtSet: new Set(),
  cafeSet: new Set(),
  categorySet: new Set(),
  subcatSet: new Set(),
  priceMin: '',
  priceMax: '',
});

const activeDistrictSet = appliedFilters.districtSet;
const activeCafeSet = appliedFilters.cafeSet;
const activeCategorySet = appliedFilters.categorySet;
const activeSubcatSet = appliedFilters.subcatSet;
const activePriceMin = appliedFilters.priceMin;
const activePriceMax = appliedFilters.priceMax;

// ——— пагінація результатів з сервера ———
const PAGE_SIZE = getResponsivePageSize();
const [items, setItems] = useState([]);
const [cursor, setCursor] = useState(null);
const [hasMore, setHasMore] = useState(true);
const [loadingPage, setLoadingPage] = useState(false);
const hasTemplateFilters = activeCategorySet.size > 0 || activeSubcatSet.size > 0;
const templateCatalogMap = useMemo(() => {
  const map = new Map();
  templateCatalog.forEach((template) => {
    if (template?.id) map.set(template.id, template);
  });
  return map;
}, [templateCatalog]);

// ключ активного запиту: якщо змінився — попередні відповіді ігноруємо
const reqKeyRef = useRef(0);

const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const mergeTemplateCatalog = (fetchedTemplates = []) => {
  if (!fetchedTemplates.length) return;
  setTemplateCatalog((prev) => {
    const next = new Map(prev.map((template) => [template.id, template]));
    let changed = false;
    fetchedTemplates.forEach((template) => {
      if (!template?.id) return;
      if (!next.has(template.id)) changed = true;
      next.set(template.id, template);
    });
    if (!changed) return prev;
    const merged = Array.from(next.values());
    if (filtersBootstrapCache) {
      filtersBootstrapCache = {
        ...filtersBootstrapCache,
        templateCatalog: merged,
      };
    }
    return merged;
  });
};

const loadTemplatesByIds = async (templateIds, myKey) => {
  const uniqueTemplateIds = Array.from(new Set((templateIds || []).filter(Boolean)));
  if (uniqueTemplateIds.length === 0) return new Map();

  const map = new Map();
  const missingIds = [];
  uniqueTemplateIds.forEach((id) => {
    const cachedTemplate = templateCatalogMap.get(id);
    if (cachedTemplate) map.set(id, cachedTemplate);
    else missingIds.push(id);
  });

  if (missingIds.length === 0) return map;

  const fetchedTemplates = [];
  for (const pack of chunk(missingIds, 10)) {
    if (pack.length === 0) continue;
    const snap = await getDocs(
      query(collection(db, 'bookTemplates'), where(documentId(), 'in', pack))
    );
    if (reqKeyRef.current !== myKey) return null;
    snap.docs.forEach((doc) => {
      const template = { id: doc.id, ...doc.data() };
      map.set(doc.id, template);
      fetchedTemplates.push(template);
    });
  }

  if (fetchedTemplates.length && reqKeyRef.current === myKey) {
    mergeTemplateCatalog(fetchedTemplates);
  }

  return map;
};

const excludeReservedBooks = async (rawBooks, myKey) => {
  if (!rawBooks?.length) return [];
  const reservationRows = await listReservationsByBookIds(rawBooks.map((b) => b.id));
  if (reqKeyRef.current !== myKey) return null;
  const reservedBookIds = new Set(
    (reservationRows || [])
      .filter((r) => isReservationActive(r))
      .map((r) => String(r.bookId || r.id))
  );
  return rawBooks.filter((b) => !reservedBookIds.has(String(b.id)));
};

const loadTemplateFilteredAll = async (myKey) => {
  const min = (activePriceMin === '' ? (priceBounds?.[0] ?? 0) : Number(activePriceMin));
  const max = (activePriceMax === '' ? (priceBounds?.[1] ?? 10000) : Number(activePriceMax));

  const selectedCats = categories.filter(c => activeCategorySet.has(c.toLowerCase()));
  const selectedSubs = subcategories.filter(s => activeSubcatSet.has(s.toLowerCase()));
  const selectedSubsLC = new Set(selectedSubs.map(s => s.toLowerCase()));

  // 1) Витягуємо релевантні шаблони з кешу (fallback: один запит у Firestore).
  let templates = templateCatalog;
  if (!templates.length) {
    const fallbackSnap = await getDocs(query(collection(db, 'bookTemplates'), limit(1000)));
    if (reqKeyRef.current !== myKey) return;
    templates = fallbackSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    mergeTemplateCatalog(templates);
  }
  if (selectedCats.length > 0) {
    const catsLC = new Set(selectedCats.map(c => c.toLowerCase()));
    templates = templates.filter(t => catsLC.has(String(t.category || '').toLowerCase()));
  }
  if (selectedSubs.length > 0) {
    templates = templates.filter(t => selectedSubsLC.has(String(t.subcategory || '').toLowerCase()));
  }

  const tplIds = templates.map(t => t.id);
  const tplMap = new Map(templates.map(t => [t.id, t]));
  if (tplIds.length === 0) {
    setItems([]);
    setCursor(null);
    setHasMore(false);
    return;
  }

  // 2) Витягуємо books по templateId (серверно), решту вузьких фільтрів добиваємо на клієнті.
  const rawBooks = [];
  for (const pack of chunk(tplIds, 10)) {
    let q = query(
      collection(db, 'books'),
      where('status', '==', 'available'),
      where('templateId', 'in', pack),
      limit(200)
    );
    if (activeCafeSet.size > 0 && activeCafeSet.size <= 10) {
      q = query(q, where('cafeName', 'in', Array.from(activeCafeSet)));
    }
    const s = await getDocs(q);
    if (reqKeyRef.current !== myKey) return;
    rawBooks.push(...s.docs.map(d => ({ id: d.id, ...d.data(), __col: 'books' })));
  }
  const visibleRawBooks = await excludeReservedBooks(rawBooks, myKey);
  if (!visibleRawBooks) return;

  // 3) Мердж + дедуп + фільтр по ціні.
  const seen = new Set();
  const merged = [];
  for (const b of visibleRawBooks) {
    const dedupKey = b.id || `${b.templateId}:${b.cafeName || ''}:${b.isbn || ''}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    const tpl = tplMap.get(b.templateId) || {};
    const borrowPrice = b.borrowPrice ?? tpl.borrowPrice ?? b.price ?? tpl.price ?? null;
    const pNum = Number(borrowPrice);
    if (!Number.isFinite(pNum) || pNum < min || pNum > max) continue;

    const cover = b.coverUrl || b.cover || tpl.coverUrl || tpl.cover || tpl.imageUrl || tpl.thumbnailUrl || '';
    merged.push({
      ...b,
      isbn: b.isbn ?? tpl.isbn ?? '',
      title: b.title ?? tpl.title ?? 'Без назви',
      author: b.author ?? tpl.author ?? '',
      coverUrl: cover,
      cover,
      borrowPrice,
      borrowCount: b.borrowCount ?? tpl.borrowCount ?? 0,
      category: b.category ?? tpl.category ?? '',
      subcategory: b.subcategory ?? tpl.subcategory ?? '',
    });
  }

  // Новіші зверху (fallback: createdAt строкою, якщо немає createdAtTs)
  merged.sort((a, b) => {
    const aTs = a.createdAtTs?.toMillis?.() ?? (Date.parse(a.createdAt) || 0);
    const bTs = b.createdAtTs?.toMillis?.() ?? (Date.parse(b.createdAt) || 0);
    return bTs - aTs;
  });

  setItems(merged);
  setCursor(null);
  setHasMore(false);
};

// безпечне скидання + автозавантаження першої сторінки
const resetAndLoadFirstPage = async () => {
  reqKeyRef.current += 1;
  const myKey = reqKeyRef.current;

  setItems([]);
  setCursor(null);
  setHasMore(true);
  setLoadingPage(true);

  try {
    if (hasTemplateFilters) {
      await loadTemplateFilteredAll(myKey);
      return;
    }

    const snap = await getDocs(buildQuery(null));
    if (reqKeyRef.current !== myKey) return; // застаріла відповідь — ігноруємо

    // 1) Сирі екземпляри з books (ця сторінка)
    const rawBooks = snap.docs.map(d => ({ id: d.id, ...d.data(), __col: 'books' }));
    const visibleRawBooks = await excludeReservedBooks(rawBooks, myKey);
    if (!visibleRawBooks) return;

    // 2) Збираємо унікальні templateId з цієї порції
    const tplIds = Array.from(new Set(
      visibleRawBooks.map(b => b.templateId).filter(Boolean)
    ));

    // 3) Шаблони беремо з каталогу; у Firestore йдемо тільки за відсутніми templateId.
    const tplMap = await loadTemplatesByIds(tplIds, myKey);
    if (!tplMap) return;

    // 4) Формуємо сторінку збагачених записів
    const page = visibleRawBooks.map(b => {
      const tpl = tplMap.get(b.templateId) || {};
      const cover = b.coverUrl || b.cover || tpl.coverUrl || tpl.cover || tpl.imageUrl || tpl.thumbnailUrl || '';
      return {
        ...b,
        // підкинемо дефолти з шаблону, якщо в books немає
        isbn: b.isbn ?? tpl.isbn ?? '',
        title: b.title ?? tpl.title ?? 'Без назви',
        author: b.author ?? tpl.author ?? '',
        coverUrl: cover,   // ← потрібне для обкладинки
        cover,
        borrowPrice: b.borrowPrice ?? tpl.borrowPrice ?? b.price ?? null,
        borrowCount: b.borrowCount ?? tpl.borrowCount ?? 0
      };
    });

    setItems(page);
    setCursor(snap.docs[snap.docs.length - 1] || null);
    setHasMore(snap.size === PAGE_SIZE);
  } finally {
    if (reqKeyRef.current === myKey) setLoadingPage(false);
  }
};



// === серверний запит: застосовуємо фільтри НА СЕРВЕРІ ===
const buildQuery = (afterDoc = null) => {
  const min = (activePriceMin === '' ? (priceBounds?.[0] ?? 0) : Number(activePriceMin));
  const max = (activePriceMax === '' ? (priceBounds?.[1] ?? 10000) : Number(activePriceMax));

  // базова частина: статус + ціновий коридор
  let q = query(
    collection(db, 'books'),
    where('status', '==', 'available'),
    where('borrowPrice', '>=', min),
    where('borrowPrice', '<=', max)
  );

  // Кав’ярні (до 10 значень для 'in')
  if (activeCafeSet.size > 0 && activeCafeSet.size <= 10) {
    q = query(q, where('cafeName', 'in', Array.from(activeCafeSet)));
  }

  // Категорія/підкатегорія фільтруються на клієнті (pagedFiltered),
  // щоб не вимагати нові composite index для кожної комбінації.

  // Чисто серверне індексне сортування
  q = query(q, orderBy('borrowPrice'), orderBy('createdAtTs', 'desc'), limit(PAGE_SIZE));

  if (afterDoc) q = query(q, startAfter(afterDoc));
  return q;
};




// === завантаження наступної сторінки з сервера ===
const loadServerPage = async () => {
  if (loadingPage || !hasMore) return;
  setLoadingPage(true);
  const myKey = reqKeyRef.current;

  try {
    const snap = await getDocs(buildQuery(cursor));
    if (reqKeyRef.current !== myKey) return; // під час відповіді фільтри змінилися

    // 1) Сирі екземпляри
    const rawBooks = snap.docs.map(d => ({ id: d.id, ...d.data(), __col: 'books' }));
    const visibleRawBooks = await excludeReservedBooks(rawBooks, myKey);
    if (!visibleRawBooks) return;

    // 2) Шаблони беремо з каталогу; у Firestore йдемо тільки за відсутніми templateId.
    const tplIds = Array.from(new Set(visibleRawBooks.map(b => b.templateId).filter(Boolean)));
    const tplMap = await loadTemplatesByIds(tplIds, myKey);
    if (!tplMap) return;

    // 3) Збагачуємо
    const page = visibleRawBooks.map(b => {
      const tpl = tplMap.get(b.templateId) || {};
      const cover = b.coverUrl || b.cover || tpl.coverUrl || tpl.cover || '';
      return {
        ...b,
        title: b.title ?? tpl.title ?? 'Без назви',
        author: b.author ?? tpl.author ?? '',
        coverUrl: cover,
        cover,                           // ← додаткове поле для сумісності
        borrowPrice: b.borrowPrice ?? tpl.borrowPrice ?? b.price ?? null,
        borrowCount: b.borrowCount ?? tpl.borrowCount ?? 0
      };
    });

    // мержимо без дублікатів
    setItems(prev => {
      const seen = new Set(prev.map(x => x.id));
      const merged = prev.slice();
      for (const b of page) if (!seen.has(b.id)) merged.push(b);
      return merged;
    });


    setCursor(snap.docs[snap.docs.length - 1] || null);
    setHasMore(snap.size === PAGE_SIZE);
  } finally {
    if (reqKeyRef.current === myKey) setLoadingPage(false);
  }
};





// === client-side фільтр по вибраних чекбоксах ===
const pagedFiltered = useMemo(() => {
  let list = items;
  // показуємо лише доступні екземпляри
  list = list.filter(b => (b.status || '').toLowerCase() === 'available');

  if (activeDistrictSet.size) {
    list = list.filter(b => {
      const slug = toDistrictSlug(cafeDistrictByName.get(b.cafeName));
      return slug && activeDistrictSet.has(slug);
    });
  }

  if (activeCafeSet.size) {
    list = list.filter(b => activeCafeSet.has((b.cafeName || '').trim()));
  }

  if (activeCategorySet.size) {
    const sel = activeCategorySet;
    list = list.filter(b => sel.has(String(b.category || '').toLowerCase()));
  }

  if (activeSubcatSet.size) {
    const sel = activeSubcatSet;
    list = list.filter(b => sel.has(String(b.subcategory || '').toLowerCase()));
  }

  return list;
}, [items, activeDistrictSet, activeCafeSet, activeCategorySet, activeSubcatSet, cafeDistrictByName]);

// Якщо відфільтрований список порожній, але сторінки ще є — довантажуємо автоматично
useEffect(() => {
  if (loading || loadingPage) return;
  if (pagedFiltered.length === 0 && items.length > 0 && hasMore) {
    loadServerPage();
  }
  // коли фільтрів багато і результатів замало, можна також підвантажити,
// поки не наберемо хоча б одну «рядку» карток (опційно):
// else if (pagedFiltered.length > 0 && pagedFiltered.length < 8 && hasMore) {
//   loadServerPage();
// }
}, [
  // важливо: реагуємо і на зміну відфільтрованого списку
  pagedFiltered.length,
  items.length,
  hasMore,
  loading,
  loadingPage
]);




/* ---------- стилі (адаптив) ---------- */
const Styles = () => (
  <style>{`
    .wrap { display:block; padding:16px; }
    .aside {
      position: fixed;
      top: var(--aside-top, 76px);
      left: 16px;
      width: 320px;
      background:#fff;
      border:1px solid #e5e7eb;
      border-radius:12px;
      padding:16px;
      max-height: calc(100vh - var(--aside-top, 76px) - 16px);
      overflow: auto;
    }
    .wrap > main { margin-left: 336px; }
    .page-filters { background:#F6F4EF; min-height:100vh; }
    .flt-section { border-bottom:1px solid #f1f5f9; padding:10px 0; }
    .flt-head { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .flt-title-wrap { display:flex; align-items:center; gap:10px; }
    .flt-title { font-weight:800; }
    .flt-count { color:#64748b; font-weight:600; }
    .flt-chevron { border:none; background:transparent; color:#64748b; padding:6px; cursor:pointer; }
    .flt-body { margin-top:10px; display:grid; gap:8px; }
    .mobile-filters-sticky-head {
      position: fixed;
      top: var(--mobile-head-top, 0px);
      left: 0;
      right: 0;
      z-index: 11;
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      gap: 8px;
      align-items: center;
      background: #fff;
      padding: 8px 12px;
      border-bottom: 1px solid #e5e7eb;
      box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
    }
    .mobile-results-head {
      position: sticky;
      top: 0;
      z-index: 6;
      background: #F6F4EF;
      padding: 8px 0;
      margin-bottom: 4px;
    }
    .mobile-results-chips {
      position: sticky;
      top: 44px;
      z-index: 5;
      background: #F6F4EF;
      padding-bottom: 8px;
    }
    .mobile-results-head-fixed {
      position: fixed;
      left: 0;
      right: 0;
      top: var(--mobile-head-top, 0px);
      z-index: 8;
      background: #F6F4EF;
      border-bottom: 1px solid #e5e7eb;
      padding: 8px 12px;
    }
    .mobile-results-head-inner {
      max-width: 1100px;
      margin: 0 auto;
      display: grid;
      gap: 8px;
    }

    .row2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }

    /* ВАЖЛИВО: інпути всередині фільтра не виходять за межі */
    .num,
    .flt-input,
    .flt-select {
      width: 100%;
      height: 36px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;           /* ← вирішує переповнення */
    }

    .grid-1 { display:grid; grid-template-columns:1fr; gap:8px; max-height:260px; overflow:auto; padding-right:4px; }
    .check-item {
      display:flex;
      align-items:center;
      gap:12px;
      min-height:44px;
      padding:10px 12px;
      border:1px solid #e5e7eb;
      border-radius:12px;
      cursor:pointer;
      transition: border-color .16s ease, background-color .16s ease;
    }
    .check-item:hover {
      border-color:#d1d5db;
      background:#f8fafc;
    }
    .check-item input[type="checkbox"] {
      width:18px;
      height:18px;
      margin:0;
      flex:0 0 auto;
      accent-color:#0b5226;
    }
    .results { display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap:20px; }

    @media (max-width: 768px) {
      .wrap { display:block; padding:0; }
      .aside {
        position: static;
        width: auto;
        left: auto;
        top: auto;
        max-height: none;
        overflow: visible;
        padding: 12px;
        border-left: 0;
        border-right: 0;
        border-radius: 0;
      }
      .wrap > main { margin-left: 0; }
      .results { grid-template-columns: repeat(2, minmax(0,1fr)); gap:12px; }
      .grid-1 { max-height: none; overflow: visible; padding-right: 0; }
    }
    @media (max-width: 360px) {
      .results { grid-template-columns: 1fr; }
    }

    
    /* шапка секції фільтрів */
    .flt-top { display:flex; align-items:center; justify-content:space-between; margin:0 0 8px; }
    .flt-top-title { margin:0; }
    .flt-top-toggle {
      border:1px solid #e5e7eb;
      background:#fff;
      color:#334155;
      width:36px; height:36px;
      border-radius:8px;
      display:flex; align-items:center; justify-content:center;
      cursor:pointer;
    }

    /* коли фільтри згорнуті — залишаємо тільки шапку */
    .wrap.collapsed { grid-template-columns: 1fr; }
    .wrap.collapsed .aside { display:block; padding:12px; }
    .wrap.collapsed .aside .flt-section,
    .wrap.collapsed .aside .flt-bottom { display:none; }

    /* 4 картки в ряд на десктопі, коли фільтри згорнуті */
    .wrap.collapsed .results {
      display:grid;
      grid-template-columns: repeat(4, minmax(0,1fr));
      gap:20px;
    }


    /* Адаптив — щоб на вузьких екранах не ламалось */
    @media (max-width: 1200px) {
      .wrap.collapsed .results { grid-template-columns: repeat(3, minmax(0,1fr)); }
    }
    @media (max-width: 900px) {
      .wrap.collapsed .results { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 540px) {
      .wrap.collapsed .results { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 360px) {
      .wrap.collapsed .results { grid-template-columns: 1fr; }
    }

  `}</style>

);

  /* ---------- завантаження довідників ---------- */
  useEffect(() => {
    let cancelled = false;
    const now = Date.now();
    if (filtersBootstrapCache && now - filtersBootstrapCache.ts < FILTERS_BOOTSTRAP_TTL_MS) {
      setCafes(filtersBootstrapCache.cafes);
      setCategories(filtersBootstrapCache.categories);
      setSubcategories(filtersBootstrapCache.subcategories);
      setTemplateCatalog(filtersBootstrapCache.templateCatalog);
      setPriceBounds(filtersBootstrapCache.bounds);
      setPriceMin('');
      setPriceMax('');
      setLoading(false);
      return () => { cancelled = true; };
    }
    (async () => {
      // кафе
      const cSnap = await getDocs(collection(db, 'cafes'));
      const cafesArr = [];
      cSnap.forEach(d => {
        const x = d.data();
        const name = x.cafeName || '';
        const address = x.cafeAddress || '';

        // беремо slug з поля БД або нормалізуємо
        let districtSlug = toDistrictSlug(x.districtSlug || x.district || x.area);

        // якщо після нормалізації slug порожній — НЕ додаємо
        const districtLabel = districtSlug ? (DISTRICT_LABEL[districtSlug] || districtSlug) : '';

        cafesArr.push({ id: d.id, name, address, district: districtSlug, districtLabel });

      });

      // категорії/підкатегорії та межі цін — з шаблонів
      const tSnap = await getDocs(query(collection(db, 'bookTemplates'), limit(800)));
      const templatesArr = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const catSet = new Set();
      const subSet = new Set();
      let min = Infinity, max = 0;
      templatesArr.forEach((x) => {
        if (x.category) catSet.add(String(x.category).trim());
        if (x.subcategory) subSet.add(String(x.subcategory).trim());
        const p = Number(x.borrowPrice ?? x.price);
        if (Number.isFinite(p)) { if (p < min) min = p; if (p > max) max = p; }
      });

      if (!cancelled) {
        const sortedCafes = cafesArr.sort((a,b)=>a.name.localeCompare(b.name));
        const sortedCategories = Array.from(catSet).sort((a,b)=>a.localeCompare(b));
        const sortedSubcategories = Array.from(subSet).sort((a,b)=>a.localeCompare(b));
        setCafes(sortedCafes);
        setCategories(sortedCategories);
        setSubcategories(sortedSubcategories);
        setTemplateCatalog(templatesArr);

        const bounds = [
          Number.isFinite(min) ? Math.max(0, Math.floor(min)) : 0,
          Number.isFinite(max) ? Math.ceil(max) : 400,
        ];
        filtersBootstrapCache = {
          ts: Date.now(),
          cafes: sortedCafes,
          categories: sortedCategories,
          subcategories: sortedSubcategories,
          templateCatalog: templatesArr,
          bounds,
        };
        setPriceBounds(bounds);     
        setPriceMin(''); // залишаємо поля порожні
        setPriceMax('');
      }

    })().finally(() => {
    if (!cancelled) {
      // лише позначаємо, що довідники/межі готові.
      // ПЕРШЕ завантаження зробить нижній ефект, який чекає на loading=false + валідні межі.
      setLoading(false);
    }
  });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const recalcAsideTop = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileFiltersOpen(true);
      const headerEl = document.querySelector('header');
      if (!headerEl) {
        setMobileHeadTop(0);
        if (mobile) {
          setAsideTop(76);
          return;
        }
        setAsideTop(12);
        return;
      }

      const rect = headerEl.getBoundingClientRect();
      const mobileTop = rect.bottom > 0 ? Math.round(rect.bottom) : 0;
      setMobileHeadTop((prev) => (prev === mobileTop ? prev : mobileTop));
      if (mobile) {
        setAsideTop(76);
        return;
      }
      const nextTop = rect.bottom > 0 ? Math.max(12, Math.round(rect.bottom + 8)) : 12;
      setAsideTop((prev) => (prev === nextTop ? prev : nextTop));
    };

    recalcAsideTop();
    window.addEventListener('scroll', recalcAsideTop, { passive: true });
    window.addEventListener('resize', recalcAsideTop);
    return () => {
      window.removeEventListener('scroll', recalcAsideTop);
      window.removeEventListener('resize', recalcAsideTop);
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (!user?.uid) {
        setFavoriteCafeIds([]);
        return;
      }
      try {
        const ids = await getFavoriteCafeIds(user.uid);
        setFavoriteCafeIds(Array.isArray(ids) ? ids : []);
      } catch {
        setFavoriteCafeIds([]);
      }
    })();
  }, [user?.uid]);


  /* ---------- завантаження книг (порційно) ---------- */
// 1) Автопідвантаження ПЕРШОЇ сторінки одразу після того,
// як підвантажилися довідники і ми знаємо межі цін
useEffect(() => {
  if (loading) return;               // чекаємо поки підтягнуться cafes/templates і виставляться priceMin/priceMax
  resetAndLoadFirstPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [loading, appliedFilters]);




  /* ---------- дії ---------- */
  const toggleSet = (setUpdater, value, normalizer = (v)=>v) => {
    setUpdater(prev => {
      const next = new Set(prev);
      const key = normalizer(value);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const resetAll = () => {
    setPriceMin('');
    setPriceMax('');
    setDistrictSet(new Set());
    setCafeSet(new Set());
    setCategorySet(new Set());
    setSubcatSet(new Set());
    setAppliedFilters({
      districtSet: new Set(),
      cafeSet: new Set(),
      categorySet: new Set(),
      subcatSet: new Set(),
      priceMin: '',
      priceMax: '',
    });
  };

  const applyFilters = () => {
    setAppliedFilters({
      districtSet: new Set(districtSet),
      cafeSet: new Set(cafeSet),
      categorySet: new Set(categorySet),
      subcatSet: new Set(subcatSet),
      priceMin,
      priceMax,
    });
    if (isMobile) setMobileFiltersOpen(false);
  };

  const favoriteCafeNames = useMemo(
    () => cafes.filter((c) => favoriteCafeIds.includes(c.id)).map((c) => c.name).filter(Boolean),
    [cafes, favoriteCafeIds]
  );
  const hasFavoriteCafes = favoriteCafeNames.length > 0;
  const favoritesFilterSelected =
    hasFavoriteCafes &&
    cafeSet.size === favoriteCafeNames.length &&
    favoriteCafeNames.every((name) => cafeSet.has(name));

  const toggleFavoriteCafesInDraft = () => {
    if (!hasFavoriteCafes) return;
    setCafeSet(favoritesFilterSelected ? new Set() : new Set(favoriteCafeNames));
  };

  const openMobileFilters = () => {
    setMobileFiltersOpen(true);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const activeFilterChips = useMemo(() => {
    const chips = [];

    activeDistrictSet.forEach((slug) => {
      chips.push({
        kind: 'district',
        value: slug,
        label: DISTRICT_LABEL[slug] || slug,
      });
    });

    activeCafeSet.forEach((name) => {
      chips.push({
        kind: 'cafe',
        value: name,
        label: name,
      });
    });

    activeCategorySet.forEach((key) => {
      const raw = categories.find((c) => c.toLowerCase() === key) || key;
      chips.push({
        kind: 'category',
        value: key,
        label: mapCategory(raw),
      });
    });

    activeSubcatSet.forEach((key) => {
      const raw = subcategories.find((s) => s.toLowerCase() === key) || key;
      chips.push({
        kind: 'subcat',
        value: key,
        label: raw,
      });
    });

    if (activePriceMin !== '' || activePriceMax !== '') {
      const minTxt = activePriceMin === '' ? 'від 0' : `від ${activePriceMin}`;
      const maxTxt = activePriceMax === '' ? 'до max' : `до ${activePriceMax}`;
      chips.push({
        kind: 'price',
        value: 'price',
        label: `Ціна: ${minTxt}, ${maxTxt}`,
      });
    }

    return chips;
  }, [activeDistrictSet, activeCafeSet, activeCategorySet, activeSubcatSet, activePriceMin, activePriceMax, categories, subcategories]);

  const removeAppliedChip = (chip) => {
    const nextDistrict = new Set(activeDistrictSet);
    const nextCafe = new Set(activeCafeSet);
    const nextCategory = new Set(activeCategorySet);
    const nextSubcat = new Set(activeSubcatSet);
    let nextMin = activePriceMin;
    let nextMax = activePriceMax;

    if (chip.kind === 'district') nextDistrict.delete(chip.value);
    if (chip.kind === 'cafe') nextCafe.delete(chip.value);
    if (chip.kind === 'category') nextCategory.delete(chip.value);
    if (chip.kind === 'subcat') nextSubcat.delete(chip.value);
    if (chip.kind === 'price') {
      nextMin = '';
      nextMax = '';
    }

    setDistrictSet(nextDistrict);
    setCafeSet(nextCafe);
    setCategorySet(nextCategory);
    setSubcatSet(nextSubcat);
    setPriceMin(nextMin);
    setPriceMax(nextMax);
    setAppliedFilters({
      districtSet: nextDistrict,
      cafeSet: nextCafe,
      categorySet: nextCategory,
      subcatSet: nextSubcat,
      priceMin: nextMin,
      priceMax: nextMax,
    });
  };


  if (loading && !books.length) {
    return (
      <>
        <Styles />
        <div style={{ padding: 16 }}>Завантаження…</div>
      </>
    );
  }

  /* ---------- рендер ---------- */
  return (
    <>
      <Styles />
      <div className="page-filters" style={{ '--aside-top': `${asideTop}px`, '--mobile-head-top': `${mobileHeadTop}px` }}>
        {isMobile && !mobileFiltersOpen && (
          <div className="mobile-results-head-fixed">
            <div className="mobile-results-head-inner">
              <button
                type="button"
                onClick={openMobileFilters}
                style={{
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  borderRadius: 999,
                  padding: '6px 14px',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: 17,
                  justifySelf: 'center',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M3 5h18l-7 8v5l-4 2v-7L3 5z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Фільтр
              </button>
              {activeFilterChips.length > 0 && (
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
                  {activeFilterChips.map((chip) => (
                    <button
                      key={`top-${chip.kind}:${chip.value}`}
                      type="button"
                      onClick={() => removeAppliedChip(chip)}
                      title="Прибрати фільтр"
                      style={{
                        border: '1px solid #d1d5db',
                        background: '#fff',
                        borderRadius: 999,
                        padding: '6px 10px',
                        fontSize: 13,
                        color: '#111827',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span>{chip.label}</span>
                      <span style={{ color: '#6b7280' }}>×</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="wrap">
        {/* ЛІВЕ МЕНЮ */}
        <aside
          className="aside"
          style={
            isMobile
              ? (mobileFiltersOpen ? { paddingTop: 64 } : { display: 'none' })
              : undefined
          }
        >
          {isMobile ? (
            <div className="mobile-filters-sticky-head">
              <button
                onClick={resetAll}
                style={{
                  justifySelf: 'start',
                  height: 34,
                  padding: '0 12px',
                  borderRadius: 10,
                  border: '1.5px solid #0b5226',
                  background: '#f3faf6',
                  color: '#0b5226',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Скинути
              </button>
              <div style={{ fontWeight: 800 }}>Фільтр</div>
              <button
                onClick={applyFilters}
                style={{ justifySelf: 'end', height: 34, padding: '0 12px', borderRadius: 8, border: '1px solid #0b5226', background: '#0b5226', color: '#fff', cursor: 'pointer', fontWeight: 700 }}
              >
                Застосувати
              </button>
            </div>
          ) : (
            <div className="flt-top" style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
              <h2 className="flt-top-title" style={{margin:0}}>Фільтри</h2>
              <button
                onClick={resetAll}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 10,
                  border: '1.5px solid #0b5226',
                  background: '#f3faf6',
                  color: '#0b5226',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Скинути
              </button>
            </div>
          )}



          {/* Ціна */}
          <MultiSection title="Ціна (монет)" defaultOpen={false} totalCount={districts.length}>
            <div className="row2">
              <label>
                <div style={{ fontSize:12, color:'#6b7280', marginBottom:4 }}>від</div>
                <input
                  className="num"
                  type="number"
                  value={priceMin}
                  placeholder={String(priceBounds?.[0] ?? 0)}
                  onChange={e => {
                    const v = e.target.value;
                    setPriceMin(v === '' ? '' : Number(v));
                  }}
                />

                <input
                  className="num"
                  type="number"
                  value={priceMax}
                  placeholder={String(priceBounds?.[1] ?? 10000)}
                  onChange={e => {
                    const v = e.target.value;
                    setPriceMax(v === '' ? '' : Number(v));
                  }}
                />


              </label>
            </div>
          </MultiSection>

          {/* Райони — мультивибір */}
          <MultiSection title="Район" totalCount={districts.length}>
            <div className="grid-1">
              {districts.map(slug => {
                const key = slug; // key і значення — slug
                const label = DISTRICT_LABEL[slug] || slug;
                return (
                  <label key={slug} className="check-item">
                    <input
                      type="checkbox"
                      checked={districtSet.has(key)}
                      onChange={() => toggleSet(setDistrictSet, key)}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </MultiSection>


          {/* Кав'ярні — мультивибір */}
          <MultiSection
            title="Кав'ярні"
            totalCount={cafes.length}
           
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <button
                type="button"
                onClick={toggleFavoriteCafesInDraft}
                disabled={!hasFavoriteCafes}
                style={{
                  border: favoritesFilterSelected ? '1px solid #0b5226' : '1px solid #d1d5db',
                  background: favoritesFilterSelected ? '#ecfdf3' : '#fff',
                  color: favoritesFilterSelected ? '#14532d' : '#111827',
                  borderRadius: 999,
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: hasFavoriteCafes ? 'pointer' : 'default',
                }}
                title={hasFavoriteCafes ? 'Обрати улюблені кав’ярні' : 'Спочатку додайте кав’ярні в улюблені'}
              >
                {favoritesFilterSelected ? 'Улюблені вибрано' : 'Обрати улюблені'}
              </button>
              {!hasFavoriteCafes && (
                <span style={{ fontSize: 12, color: '#6b7280' }}>Немає улюблених</span>
              )}
            </div>
            <div className="grid-1">
              {cafes.map(c => (
                <label key={c.name} className="check-item">
                  <input
                    type="checkbox"
                    checked={cafeSet.has(c.name)}
                    onChange={() => toggleSet(setCafeSet, c.name)}
                  />
                  <div>
                    <div style={{ fontWeight:600 }}>{c.name}</div>
                    {c.address && <div style={{ fontSize:12, color:'#6b7280' }}>{c.address}</div>}
                  </div>
                </label>
              ))}
            </div>
          </MultiSection>

          {/* Категорія — мультивибір (з відображенням людських назв) */}
          <MultiSection
            title="Категорія"
            totalCount={categories.length}
          >
            <div className="grid-1">
              {categories.map(c => {
                const key = c.toLowerCase();
                return (
                  <label key={c} className="check-item">
                    <input
                      type="checkbox"
                      checked={categorySet.has(key)}
                      onChange={() => toggleSet(setCategorySet, key)}
                    />
                    <span>{mapCategory(c)}</span>
                  </label>
                );
              })}
            </div>
          </MultiSection>

          {/* Підкатегорія — мультивибір */}
          <MultiSection
            title="Підкатегорія"
            totalCount={subcategories.length}
            onToggleAll={(checked) => checked ? setSubcatSet(new Set()) : setSubcatSet(new Set())}
          >
            <div className="grid-1">
              {subcategories.map(s => {
                const key = s.toLowerCase();
                return (
                  <label key={s} className="check-item">
                    <input
                      type="checkbox"
                      checked={subcatSet.has(key)}
                      onChange={() => toggleSet(setSubcatSet, key)}
                    />
                    <span>{s}</span>
                  </label>
                );
              })}
            </div>
          </MultiSection>

          {!isMobile && (
            <div className="flt-bottom" style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button
                onClick={applyFilters}
                style={{
                  padding: '8px 14px',
                  height: 'auto',
                  borderRadius: 10,
                  border: '1px solid #0b5226',
                  background: '#0b5226',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  whiteSpace: 'nowrap',
                  fontSize: 14,
                  lineHeight: 1.2,
                  fontWeight: 700,
                }}
              >
                Застосувати
              </button>
            </div>
          )}


        </aside>

        {/* ПРАВА КОЛОНКА — РЕЗУЛЬТАТИ */}
        <main style={isMobile && !mobileFiltersOpen ? { paddingTop: activeFilterChips.length > 0 ? 96 : 56, paddingLeft: 8, paddingRight: 8 } : undefined}>
          {activeFilterChips.length > 0 && !(isMobile && !mobileFiltersOpen) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {activeFilterChips.map((chip) => (
                <button
                  key={`${chip.kind}:${chip.value}`}
                  type="button"
                  onClick={() => removeAppliedChip(chip)}
                  title="Прибрати фільтр"
                  style={{
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    borderRadius: 999,
                    padding: '6px 10px',
                    fontSize: 13,
                    color: '#111827',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span>{chip.label}</span>
                  <span style={{ color: '#6b7280' }}>×</span>
                </button>
              ))}
            </div>
          )}
          <h2 style={{ marginTop: 0 }}>Результати: {pagedFiltered.length}</h2>

          <div className="results">
            {pagedFiltered.map((b) => {
              // нормалізуємо ключ обкладинки під те, що очікує BookCard
              const normalized = {
                ...b,
                coverUrl: b.coverUrl || b.cover || b.imageUrl || b.thumbnailUrl || '',
                cover:    b.cover    || b.coverUrl || b.imageUrl || b.thumbnailUrl || ''
              };
              return <BookCard key={b.id} book={normalized} />;

            })}
          </div>

          <div style={{ marginTop: 16, textAlign: "center" }}>
            {hasMore ? (
              <button
                onClick={loadServerPage}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  background: "#c3c6caff",
                  border: "1px solid #e5e7eb",
                }}
                disabled={loadingPage}
              >
                {loadingPage ? "Завантаження…" : "Показати ще"}
              </button>
            ) : (
              items.length > 0 && (
                <div style={{ color: "#6b7280" }}>Усе показано</div>
              )
            )}
          </div>
        </main>


      </div>
    </div>
    </>
  );
}



